import { getImpersonationFromQuery } from '../utils/url';

export const AI_SERVICE_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8004' : '';

const AI_TOKEN_EXCHANGE = `${window.location.origin}/ins/auth/token`;

const CONTENT_TYPE_JSON = 'application/json';
const CSRF_HEADER = 'X-CSRFToken';
const IMPERSONATION_USER_HEADER = 'X-Impersonation-User';
const IMPERSONATED_ORG_HEADER = 'X-Impersonated-Org';
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

// Module-level state for refresh coordination
let refreshPromise = null;

const getCookie = name => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

const INS_CSRF_COOKIE = 'interactive_service_csrf';
const DJANGO_CSRF_COOKIE = 'csrftoken';

const getCsrfToken = () => getCookie(INS_CSRF_COOKIE);
const getDjangoCsrfToken = () => getCookie(DJANGO_CSRF_COOKIE);

export const hasInteractiveServiceSession = () => Boolean(getCsrfToken());

const getImpersonationHeaders = () => {
    const headers = {};
    const impersonationUser = getImpersonationFromQuery('user');
    const impersonatedOrg = getImpersonationFromQuery('customer_id');
    if (impersonationUser) headers[IMPERSONATION_USER_HEADER] = impersonationUser;
    if (impersonatedOrg) headers[IMPERSONATED_ORG_HEADER] = impersonatedOrg;
    return headers;
};

export const ensureINSToken = async () => {
    // If a refresh is already in progress, wait for it (promise lock)
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        try {
            const response = await fetch(AI_TOKEN_EXCHANGE, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': CONTENT_TYPE_JSON,
                    'Content-Type': CONTENT_TYPE_JSON,
                    [CSRF_HEADER]: getDjangoCsrfToken(),
                    ...getImpersonationHeaders(),
                },
            });

            return { ok: response.ok, status: response.status };
        } catch {
            return { ok: false, status: null };
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};

export const handleFailedRefresh = (refreshResult) => {
    if (!refreshResult.status) {
        return
    }
    const isSessionExpired = refreshResult.status === HTTP_UNAUTHORIZED
        || refreshResult.status === HTTP_FORBIDDEN;
    if (isSessionExpired) {
        window.location.href = '/';
    }
};

export const interactiveServiceFetch = async (url, options = {}) => {
    const fullUrl = `${AI_SERVICE_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;

    const method = (options.method || 'GET').toUpperCase();

    const buildHeaders = () => {
        const headers = { ...options.headers };
        if (['POST', 'PATCH', 'DELETE', 'PUT'].includes(method)) {
            headers[CSRF_HEADER] = getCsrfToken();
        }
        return headers;
    };

    const executeFetch = () =>
        fetch(fullUrl, {
            ...options,
            method,
            credentials: 'include',
            headers: buildHeaders(),
        });

    let response = await executeFetch();

    if (response.status === HTTP_UNAUTHORIZED) {
        const refreshResult = await ensureINSToken();
        if (!refreshResult.ok) {
            handleFailedRefresh(refreshResult);
            return response;
        }
        // Retry with the new cookie
        response = await executeFetch();
    }

    return response;
};
